import { MERCHANT_OPERATIONS } from "@price-rescue/api-contracts";
import type { MerchantOperationQuery } from "@price-rescue/api-contracts";

import type { MerchantApiClient } from "../../types.js";
import type {
  BulkEligibilityResult,
  BulkEligibilityUpdate,
  EligibilityDraft,
  MerchantJobStatusDto,
  MerchantProductsListResponseData,
  ProductDetailResponse,
  ProductFilters,
  ProductRow,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

export interface ProductsApi {
  bulkUpdateEligibility(
    updates: BulkEligibilityUpdate[],
    idempotencyKey: string,
  ): Promise<BulkEligibilityResult>;
  getJob(jobId: string): Promise<MerchantJobStatusDto>;
  getProduct(productId: string): Promise<ProductDetailResponse>;
  listProducts(filters: ProductFilters): Promise<MerchantProductsListResponseData>;
  startSync(
    trigger: "MANUAL" | "RETRY",
    idempotencyKey: string,
  ): Promise<{ job: MerchantJobStatusDto; queued: boolean }>;
  updateEligibility(
    productId: string,
    draft: EligibilityDraft,
    idempotencyKey: string,
  ): Promise<ProductRow>;
}

export function createProductsApi(client: MerchantApiClient): ProductsApi {
  return {
    async bulkUpdateEligibility(updates, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<BulkEligibilityResult>>(
          MERCHANT_OPERATIONS.updateCatalogProductEligibilityBulk,
          { body: { updates }, request: { idempotencyKey } },
        ),
      );
    },
    async getJob(jobId) {
      return unwrap(
        await client.operation<DataEnvelope<MerchantJobStatusDto>>(
          MERCHANT_OPERATIONS.getMerchantJobStatus,
          { pathParams: { jobId } },
        ),
      );
    },
    async getProduct(productId) {
      return unwrap(
        await client.operation<DataEnvelope<ProductDetailResponse>>(
          MERCHANT_OPERATIONS.getCatalogProduct,
          { pathParams: { productId } },
        ),
      );
    },
    async listProducts(filters) {
      return unwrap(
        await client.operation<DataEnvelope<MerchantProductsListResponseData>>(
          MERCHANT_OPERATIONS.listCatalogProducts,
          { query: toOperationQuery(filters) },
        ),
      );
    },
    async startSync(trigger, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<{ job: MerchantJobStatusDto; queued: boolean }>>(
          MERCHANT_OPERATIONS.startMerchantProductSync,
          { body: { trigger }, request: { idempotencyKey, requiresFreshToken: true } },
        ),
      );
    },
    async updateEligibility(productId, draft, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<ProductRow>>(
          MERCHANT_OPERATIONS.updateCatalogProductEligibility,
          {
            body: {
              eligible: draft.eligible,
              reason: draft.reason,
              merchantNote: draft.merchantNote,
            },
            pathParams: { productId },
            request: { idempotencyKey },
          },
        ),
      );
    },
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function toOperationQuery(filters: ProductFilters): MerchantOperationQuery {
  return filters as unknown as MerchantOperationQuery;
}
